import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DeviceCard from './DeviceCard';
import {
  HiOutlineOfficeBuilding,
  HiOutlineLightBulb
} from 'react-icons/hi';
import { TbDoorEnter, TbSunrise } from 'react-icons/tb';

const API_URL = '/api';

function ZoneDetailPage({ token, zoneId, onBack }) {
  const [zone, setZone] = useState(null);
  const [devices, setDevices] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAutomationModal, setShowAutomationModal] = useState(false);
  const [selectedDeviceForAutomation, setSelectedDeviceForAutomation] = useState(null);
  const [automation, setAutomation] = useState(null);
  
  const [newDevice, setNewDevice] = useState({
    deviceName: '',
    hasDHT: false,
    hasOLED: false,
    relayCount: 1,
    relayType: 'toggle',
    relayNames: [''],
    // Lamba modu için
    lampMode: false,
    lamps: []
  });

  useEffect(() => {
    loadZone();
    loadDevices();
  }, [zoneId]);

  const loadZone = async () => {
    try {
      const response = await axios.get(`${API_URL}/zones/${zoneId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZone(response.data);
    } catch (err) {
      alert('Bölüm yüklenemedi: ' + err.message);
    }
  };

  const loadDevices = async () => {
    try {
      const response = await axios.get(`${API_URL}/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const zoneDevices = response.data.filter(d => {
        const deviceZoneId = d.zone?._id || d.zone;
        return deviceZoneId === zoneId;
      });
      setDevices(zoneDevices);
    } catch (err) {
      console.error('Cihazlar yüklenemedi:', err);
    }
  };

  const controlDevice = async (deviceId, relayNumber, action) => {
    try {
      await axios.post(
        `${API_URL}/devices/${deviceId}/control`,
        { relayNumber, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTimeout(loadDevices, 500);
    } catch (err) {
      alert('Komut gönderilemedi: ' + (err.response?.data?.error || err.message));
    }
  };

  const openAutomationModal = async (deviceId) => {
    setSelectedDeviceForAutomation(deviceId);
    
    try {
      const response = await axios.get(
        `${API_URL}/automations/device/${deviceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAutomation(response.data);
    } catch (err) {
      const device = devices.find(d => d._id === deviceId);
      const relayNumbers = device?.relays.filter(r => r.isEnabled).map(r => r.relayNumber) || [];
      setAutomation({
        type: 'time_based',
        relays: relayNumbers,
        timeSchedules: [],
        holidayMode: false,
        isActive: true
      });
    }
    
    setShowAutomationModal(true);
  };

  const saveAutomation = async () => {
    try {
      await axios.post(
        `${API_URL}/automations/device/${selectedDeviceForAutomation}`,
        automation,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('✅ Otomasyon kaydedildi!');
      setShowAutomationModal(false);
    } catch (err) {
      alert('❌ Hata: ' + (err.response?.data?.error || err.message));
    }
  };

  const deleteDevice = async (deviceId) => {
    if (!window.confirm('Bu cihazı tamamen silmek istediğinize emin misiniz? Bu işlem geri alınamaz!')) {
      return;
    }
    try {
      await axios.delete(
        `${API_URL}/devices/${deviceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Cihaz silindi');
      loadDevices();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAddDevice = async () => {
    try {
      let relays = [];
      
      if (newDevice.lampMode) {
        // Lamba modu - her lamba için 2 röle (aç/kapat)
        newDevice.lamps.forEach((lamp, index) => {
          const openRelayNum = index * 2 + 1;
          const closeRelayNum = index * 2 + 2;
          
          relays.push({
            relayNumber: openRelayNum,
            relayName: `${lamp.name} - AÇ`,
            relayType: 'lamp_open',
            lampGroup: index + 1,
            isEnabled: true,
            currentState: false
          });
          
          relays.push({
            relayNumber: closeRelayNum,
            relayName: `${lamp.name} - KAPAT`,
            relayType: 'lamp_close',
            lampGroup: index + 1,
            isEnabled: true,
            currentState: false
          });
        });
      } else {
        // Normal mod
        for (let i = 1; i <= newDevice.relayCount; i++) {
          relays.push({
            relayNumber: i,
            relayName: newDevice.relayNames[i - 1] || `Röle ${i}`,
            relayType: newDevice.relayType,
            isEnabled: true,
            currentState: false
          });
        }
      }

      await axios.post(
        `${API_URL}/devices`,
        {
          deviceName: newDevice.deviceName,
          facility: zone.facility,
          zone: zoneId,
          hasDHT: newDevice.hasDHT,
          hasOLED: newDevice.hasOLED,
          relays: relays
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowAddModal(false);
      setNewDevice({
        deviceName: '',
        hasDHT: false,
        hasOLED: false,
        relayCount: 1,
        relayType: 'toggle',
        relayNames: [''],
        lampMode: false,
        lamps: []
      });
      loadDevices();
    } catch (err) {
      alert('Cihaz eklenemedi: ' + (err.response?.data?.error || err.message));
    }
  };

  const updateRelayCount = (count) => {
    const names = [...newDevice.relayNames];
    while (names.length < count) {
      names.push('');
    }
    setNewDevice({ ...newDevice, relayCount: count, relayNames: names.slice(0, count) });
  };

  const updateLampCount = (count) => {
    const lamps = [...newDevice.lamps];
    while (lamps.length < count) {
      lamps.push({ name: `Lamba ${lamps.length + 1}` });
    }
    setNewDevice({ ...newDevice, lamps: lamps.slice(0, count) });
  };

  const updateLampName = (index, name) => {
    const lamps = [...newDevice.lamps];
    lamps[index] = { ...lamps[index], name };
    setNewDevice({ ...newDevice, lamps });
  };

  if (!zone) {
    return <div className="card"><h3>Yükleniyor...</h3></div>;
  }

  const selectedDevice = devices.find(d => d._id === selectedDeviceForAutomation);

  return (
    <div className="zone-detail">
      <button onClick={onBack} className="btn-back">← Geri</button>

      <div className="card">
        <h2>{zone.zoneName}</h2>
        <p style={{ color: '#999', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          Bölüm Tipi: {zone.zoneType === 'hol_ic' ? <><HiOutlineOfficeBuilding size={18} color="#3B82F6" /> HOL İÇ</> : zone.zoneType === 'hol_dis' ? <><TbDoorEnter size={18} color="#10B981" /> HOL DIŞ</> : <><HiOutlineLightBulb size={18} color="#F59E0B" /> ÇEVRE AYDINLATMA</>}
        </p>

        <div style={{ marginTop: '20px' }}>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            ➕ YENİ CİHAZ EKLE
          </button>
        </div>

        <h3 style={{ marginTop: '30px' }}>📱 Cihazlar ({devices.length})</h3>

        {devices.length === 0 ? (
          <div className="no-devices">Bu bölümde henüz cihaz yok</div>
        ) : (
          <div className="devices-grid">
            {devices.map(device => (
              <DeviceCard
                key={device._id}
                device={device}
                onControl={controlDevice}
                onDelete={deleteDevice}
                onAutomation={openAutomationModal}
              />
            ))}
          </div>
        )}
      </div>

      {/* YENİ CİHAZ EKLEME MODALI */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <h3>➕ Yeni Cihaz Ekle - {zone.zoneName}</h3>

            <div style={{ padding: '20px 0', maxHeight: '60vh', overflowY: 'auto' }}>
              <div className="form-group">
                <label>CİHAZ İSMİ *</label>
                <input
                  type="text"
                  value={newDevice.deviceName}
                  onChange={(e) => setNewDevice({ ...newDevice, deviceName: e.target.value })}
                  placeholder="Örn: ARGE, Ofis Kapısı"
                />
              </div>

              {/* LAMBA MODU TOGGLE */}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newDevice.lampMode}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewDevice({ ...newDevice, lampMode: true, lamps: [{ name: 'Lamba 1' }] });
                      } else {
                        setNewDevice({ ...newDevice, lampMode: false, lamps: [] });
                      }
                    }}
                    style={{ width: '20px', height: '20px', marginRight: '10px' }}
                  />
                  <span style={{ fontWeight: '600', color: '#FF9800', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <HiOutlineLightBulb size={18} /> Lamba Modu (Kontaktörlü - 2 Röle = 1 Lamba)
                  </span>
                </label>
                <p style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                  Her lamba için 2 röle kullanılır: Biri AÇ, biri KAPAT komutu gönderir.
                </p>
              </div>

              {/* LAMBA MODU AYARLARI */}
              {newDevice.lampMode ? (
                <>
                  <div className="form-group">
                    <label>LAMBA SAYISI</label>
                    <select
                      value={newDevice.lamps.length}
                      onChange={(e) => updateLampCount(parseInt(e.target.value))}
                    >
                      {[1, 2, 3, 4].map(n => (
                        <option key={n} value={n}>{n} Lamba ({n * 2} Röle)</option>
                      ))}
                    </select>
                  </div>

                  {newDevice.lamps.map((lamp, index) => (
                    <div key={index} style={{ 
                      padding: '15px', 
                      background: '#2D2D2D', 
                      borderRadius: '8px', 
                      marginBottom: '10px' 
                    }}>
                      <div className="form-group" style={{ marginBottom: '10px' }}>
                        <label style={{display: 'flex', alignItems: 'center', gap: '6px'}}><HiOutlineLightBulb size={16} /> LAMBA {index + 1} İSMİ</label>
                        <input
                          type="text"
                          value={lamp.name}
                          onChange={(e) => updateLampName(index, e.target.value)}
                          placeholder={`Lamba ${index + 1}`}
                        />
                      </div>
                      <div style={{ fontSize: '12px', color: '#4CAF50' }}>
                        ✓ Röle {index * 2 + 1} = AÇ komutu<br/>
                        ✓ Röle {index * 2 + 2} = KAPAT komutu
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {/* NORMAL MOD */}
                  <div className="form-group">
                    <label>RÖLE SAYISI</label>
                    <select value={newDevice.relayCount} onChange={(e) => updateRelayCount(parseInt(e.target.value))}>
                      {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} Röle</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>RÖLE TİPİ</label>
                    <select value={newDevice.relayType} onChange={(e) => setNewDevice({ ...newDevice, relayType: e.target.value })}>
                      <option value="toggle">Toggle (Sürekli Aç/Kapat)</option>
                      <option value="trigger">Trigger (1 Saniye Tetik)</option>
                    </select>
                  </div>

                  {Array.from({ length: newDevice.relayCount }, (_, i) => (
                    <div key={i} className="form-group">
                      <label>RÖLE {i + 1} İSMİ</label>
                      <input
                        type="text"
                        value={newDevice.relayNames[i] || ''}
                        onChange={(e) => {
                          const names = [...newDevice.relayNames];
                          names[i] = e.target.value;
                          setNewDevice({ ...newDevice, relayNames: names });
                        }}
                        placeholder={`Röle ${i + 1}`}
                      />
                    </div>
                  ))}
                </>
              )}

              <div className="form-group">
                <label>
                  <input type="checkbox" checked={newDevice.hasDHT} onChange={(e) => setNewDevice({ ...newDevice, hasDHT: e.target.checked })} />
                  <span style={{ marginLeft: '8px' }}>DHT22 Sensör Var</span>
                </label>
              </div>

              <div className="form-group">
                <label>
                  <input type="checkbox" checked={newDevice.hasOLED} onChange={(e) => setNewDevice({ ...newDevice, hasOLED: e.target.checked })} />
                  <span style={{ marginLeft: '8px' }}>OLED Ekran Var</span>
                </label>
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary">İPTAL</button>
              <button type="button" onClick={handleAddDevice} className="btn-primary" disabled={!newDevice.deviceName.trim()}>
                CİHAZI EKLE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTOMASYON MODAL */}
      {showAutomationModal && selectedDeviceForAutomation && selectedDevice && (
        <div className="modal-overlay" onClick={() => setShowAutomationModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3>⚙️ Otomasyon Ayarları</h3>

            <div style={{ marginBottom: '20px' }}>
              <p>Cihaz: <strong>{selectedDevice.deviceName}</strong></p>
            </div>

            {/* OTOMASYON TİPİ */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600' }}>Otomasyon Tipi:</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setAutomation({ ...automation, type: 'sun_based' })}
                  style={{
                    flex: 1, padding: '12px',
                    background: automation?.type === 'sun_based' ? '#FF9800' : '#444',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontWeight: '600',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                  }}
                >
                  <TbSunrise size={18} /> Gün Doğumu/Batımı
                </button>
                <button
                  onClick={() => setAutomation({ ...automation, type: 'time_based' })}
                  style={{
                    flex: 1, padding: '12px',
                    background: automation?.type === 'time_based' ? '#FF9800' : '#444',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontWeight: '600',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                  }}
                >
                  <span style={{fontSize: '16px'}}>⏰</span> Saatli Otomasyon
                </button>
              </div>
            </div>

            {/* SAATLİ OTOMASYON FORMU */}
            {automation?.type === 'time_based' && (
              <div style={{ marginTop: '20px', padding: '20px', background: '#2D2D2D', borderRadius: '10px' }}>
                <h4 style={{ marginBottom: '15px' }}>⏰ Zamanlama Ayarları</h4>
                
                {/* RÖLE SEÇİMİ */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600' }}>
                    Kontrol Edilecek Röleler:
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    {selectedDevice.relays.filter(r => r.isEnabled).map(relay => (
                      <label key={relay.relayNumber} style={{ display: 'flex', alignItems: 'center', padding: '10px', background: '#444', borderRadius: '5px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={automation?.relays?.includes(relay.relayNumber) || false}
                          onChange={(e) => {
                            const relays = automation?.relays || [];
                            if (e.target.checked) {
                              setAutomation({ ...automation, relays: [...relays, relay.relayNumber] });
                            } else {
                              setAutomation({ ...automation, relays: relays.filter(r => r !== relay.relayNumber) });
                            }
                          }}
                        />
                        <span style={{ marginLeft: '8px' }}>{relay.relayName}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* YENİ ZAMAN DİLİMİ EKLE */}
                <div style={{ marginBottom: '20px' }}>
                  <button
                    onClick={() => {
                      const schedules = automation?.timeSchedules || [];
                      setAutomation({
                        ...automation,
                        timeSchedules: [...schedules, { days: [1, 2, 3, 4, 5], onTime: '18:00', offTime: '08:00' }]
                      });
                    }}
                    style={{
                      padding: '10px 20px', background: '#4CAF50', color: 'white',
                      border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600'
                    }}
                  >
                    ➕ Yeni Zaman Dilimi Ekle
                  </button>
                </div>

                {/* ZAMAN DİLİMLERİ */}
                {automation?.timeSchedules?.map((schedule, index) => (
                  <div key={index} style={{ marginBottom: '20px', padding: '15px', background: '#1D1D1D', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <h5>Zaman Dilimi {index + 1}</h5>
                      <button
                        onClick={() => {
                          const schedules = automation.timeSchedules.filter((_, i) => i !== index);
                          setAutomation({ ...automation, timeSchedules: schedules });
                        }}
                        style={{
                          padding: '5px 10px', background: '#E63946', color: 'white',
                          border: 'none', borderRadius: '5px', cursor: 'pointer'
                        }}
                      >
                        🗑️
                      </button>
                    </div>

                    {/* GÜNLER */}
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Günler:</label>
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        {[
                          { value: 1, label: 'Pzt' },
                          { value: 2, label: 'Sal' },
                          { value: 3, label: 'Çar' },
                          { value: 4, label: 'Per' },
                          { value: 5, label: 'Cum' },
                          { value: 6, label: 'Cmt' },
                          { value: 0, label: 'Paz' }
                        ].map(day => (
                          <button
                            key={day.value}
                            onClick={() => {
                              const schedules = [...automation.timeSchedules];
                              const days = schedules[index].days || [];
                              if (days.includes(day.value)) {
                                schedules[index].days = days.filter(d => d !== day.value);
                              } else {
                                schedules[index].days = [...days, day.value];
                              }
                              setAutomation({ ...automation, timeSchedules: schedules });
                            }}
                            style={{
                              padding: '8px 12px',
                              background: schedule.days?.includes(day.value) ? '#FF9800' : '#444',
                              color: 'white', border: 'none', borderRadius: '5px',
                              cursor: 'pointer', fontWeight: '600'
                            }}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* AÇILIŞ - KAPANIŞ SAATİ */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Açılış Saati:</label>
                        <input
                          type="time"
                          value={schedule.onTime || ''}
                          onChange={(e) => {
                            const schedules = [...automation.timeSchedules];
                            schedules[index].onTime = e.target.value;
                            setAutomation({ ...automation, timeSchedules: schedules });
                          }}
                          style={{
                            padding: '10px', background: '#444', color: 'white',
                            border: 'none', borderRadius: '5px', width: '100%'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>Kapanış Saati:</label>
                        <input
                          type="time"
                          value={schedule.offTime || ''}
                          onChange={(e) => {
                            const schedules = [...automation.timeSchedules];
                            schedules[index].offTime = e.target.value;
                            setAutomation({ ...automation, timeSchedules: schedules });
                          }}
                          style={{
                            padding: '10px', background: '#444', color: 'white',
                            border: 'none', borderRadius: '5px', width: '100%'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {/* TATİL MODU */}
                <div style={{ marginTop: '20px', padding: '15px', background: '#1D1D1D', borderRadius: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={automation?.holidayMode || false}
                      onChange={(e) => setAutomation({ ...automation, holidayMode: e.target.checked })}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <span style={{ marginLeft: '10px', fontWeight: '600', fontSize: '16px' }}>
                      🏖️ Tatil Modu (Tüm otomasyonları durdur)
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* GÜN DOĞUMU/BATIMI FORMU */}
            {automation?.type === 'sun_based' && (
              <div style={{ marginTop: '20px', padding: '20px', background: '#2D2D2D', borderRadius: '10px' }}>
                <h4 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}><TbSunrise size={20} /> Gün Doğumu/Batımı Ayarları</h4>
                <p style={{ color: '#999', marginBottom: '15px' }}>
                  Koordinat bilgisi tesisten otomatik alınacaktır.
                </p>
                
                {/* RÖLE SEÇİMİ */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600' }}>
                    Kontrol Edilecek Röleler:
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    {selectedDevice.relays.filter(r => r.isEnabled).map(relay => (
                      <label key={relay.relayNumber} style={{ display: 'flex', alignItems: 'center', padding: '10px', background: '#444', borderRadius: '5px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={automation?.relays?.includes(relay.relayNumber) || false}
                          onChange={(e) => {
                            const relays = automation?.relays || [];
                            if (e.target.checked) {
                              setAutomation({ ...automation, relays: [...relays, relay.relayNumber] });
                            } else {
                              setAutomation({ ...automation, relays: relays.filter(r => r !== relay.relayNumber) });
                            }
                          }}
                        />
                        <span style={{ marginLeft: '8px' }}>{relay.relayName}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* GÜN DOĞUMU AYARI */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Gün Doğumunda:</label>
                  <select
                    value={automation?.sunriseAction || 'off'}
                    onChange={(e) => setAutomation({ ...automation, sunriseAction: e.target.value })}
                    style={{
                      padding: '10px', background: '#444', color: 'white',
                      border: 'none', borderRadius: '5px', width: '100%'
                    }}
                  >
                    <option value="none">Hiçbir şey yapma</option>
                    <option value="on">Röleleri AÇ</option>
                    <option value="off">Röleleri KAPAT</option>
                    <option value="trigger">Röleleri TETİKLE</option>
                  </select>
                </div>

                {/* GÜN BATIMI AYARI */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Gün Batımında:</label>
                  <select
                    value={automation?.sunsetAction || 'on'}
                    onChange={(e) => setAutomation({ ...automation, sunsetAction: e.target.value })}
                    style={{
                      padding: '10px', background: '#444', color: 'white',
                      border: 'none', borderRadius: '5px', width: '100%'
                    }}
                  >
                    <option value="none">Hiçbir şey yapma</option>
                    <option value="on">Röleleri AÇ</option>
                    <option value="off">Röleleri KAPAT</option>
                    <option value="trigger">Röleleri TETİKLE</option>
                  </select>
                </div>

                {/* TATİL MODU */}
                <div style={{ marginTop: '20px', padding: '15px', background: '#1D1D1D', borderRadius: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={automation?.holidayMode || false}
                      onChange={(e) => setAutomation({ ...automation, holidayMode: e.target.checked })}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <span style={{ marginLeft: '10px', fontWeight: '600', fontSize: '16px' }}>
                      🏖️ Tatil Modu (Tüm otomasyonları durdur)
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* BUTONLAR */}
            <div className="modal-actions" style={{ marginTop: '20px' }}>
              {automation?.type && (
                <button type="button" onClick={saveAutomation} className="btn-primary">
                  💾 KAYDET
                </button>
              )}
              <button type="button" onClick={() => setShowAutomationModal(false)} className="btn-secondary">
                KAPAT
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ZoneDetailPage;
